import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Api } from '../adapters/Api';
import type { Entitlement } from '../domain/types';
import { isBetaActive } from '../domain/beta';
import { useI18n } from '../i18n';
import { Panel, formatError } from './components';

// Pantalla de la PASARELA cuando el usuario NO tiene el plan descargable activo.
// D16 (trial sin tarjeta, doc 12 · 0.4 p6):
//  - Sin fila previa (nunca probó): CTA principal = trial de 5 días SIN tarjeta
//    (start-trial, server-side con anti-abuso); el pago queda como secundario.
//  - Con fila previa (trial consumido / sub caducada): solo el checkout, ya sin
//    trial — no hay segundo período de prueba.
// Beta gratuita (domain/beta.ts): el trial dura hasta el fin de la beta y el
// pago se oculta (Stripe en test; checkout.ts además devuelve 403).
export function Subscribe({ api, entitlement }: { api: Api; entitlement?: Entitlement }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const neverTried = !entitlement?.hasRecord;
  const beta = isBetaActive();

  const trial = useMutation({
    mutationFn: () => api.startTrial(),
    // al activarse, la query de entitlement se refresca y la pasarela abre el Dashboard
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['entitlement'] })
  });
  const checkout = useMutation({
    mutationFn: () => api.startCheckout(),
    onSuccess: ({ url }) => {
      window.location.href = url; // a Stripe Checkout (o recarga en mock)
    }
  });

  const trialErrorCode = trial.error instanceof Error ? trial.error.message : '';

  return (
    <main className="subscribe-shell">
      <Panel title={neverTried ? (beta ? t('subscribe.betaTitle') : t('subscribe.title')) : t('subscribe.endedTitle')}>
        <p className="muted">{neverTried ? t('subscribe.body') : t('subscribe.endedBody')}</p>
        <div className="status-grid">
          {/* Durante la beta se ocultan TODAS las métricas de pago/plan (dueño
              2026-08-02: nada de precio, duración de prueba ni "cancela cuando
              quieras"); solo queda "Incluye" (funciones). Reaparecen fuera de
              beta con el mismo texto. */}
          {!beta && (
            <div className="metric">
              <strong>{t('subscribe.trial')}</strong>
              <span>{t('subscribe.trialValue')}</span>
            </div>
          )}
          {!beta && (
            <div className="metric"><strong>{t('subscribe.after')}</strong><span>{t('subscribe.afterValue')}</span></div>
          )}
          <div className="metric"><strong>{t('subscribe.includes')}</strong><span>{t('subscribe.includesValue')}</span></div>
          {!beta && (
            <div className="metric"><strong>{t('subscribe.cancel')}</strong><span>{t('subscribe.cancelValue')}</span></div>
          )}
        </div>
        {neverTried ? (
          <>
            <button className="primary" disabled={trial.isPending} onClick={() => trial.mutate()}>
              {trial.isPending ? t('subscribe.trialStarting') : beta ? t('subscribe.betaTrialCta') : t('subscribe.trialCta')}
            </button>
            {/* Durante la beta no se menciona la tarjeta (se sobreentiende que
                es gratis — dueño 2026-07-27); fuera de beta se mantiene la nota
                del trial sin tarjeta. */}
            {!beta && <p className="muted">{t('subscribe.cardNote')}</p>}
            {trial.error && (
              <p className="error">
                {trialErrorCode === 'trial_already_used' ? t('subscribe.trialUsed') : t('subscribe.trialError')}
              </p>
            )}
            {!beta && (
              <button disabled={checkout.isPending} onClick={() => checkout.mutate()}>
                {checkout.isPending ? t('subscribe.ctaOpening') : t('subscribe.payCta')}
              </button>
            )}
          </>
        ) : beta ? (
          // Durante la beta nadie debería quedar sin acceso (los trials están
          // extendidos hasta el fin de la beta); si aun así alguien cae aquí,
          // se le manda al chat de soporte en vez de a un pago que fallaría.
          <p className="muted">{t('subscribe.betaEndedNote')}</p>
        ) : (
          <button className="primary" disabled={checkout.isPending} onClick={() => checkout.mutate()}>
            {checkout.isPending ? t('subscribe.ctaOpening') : t('subscribe.payCta')}
          </button>
        )}
        {checkout.error && <p className="error">{formatError(checkout.error) || t('subscribe.checkoutError')}</p>}
        <p className="muted">
          {t('subscribe.hostedNote')} <a href="https://whitebot.app">{t('subscribe.hostedLink')}</a>.
          {entitlement?.hasHostedAccount ? ' ✓' : ''}
        </p>
      </Panel>
    </main>
  );
}
